import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Card,
  Button,
  Input,
  Select,
  Table,
  Avatar,
  message,
  Spin,
  Alert,
  Modal,
  Tooltip,
} from 'antd';
import {
  CalendarOutlined,
  DollarOutlined,
  ShopOutlined,
  SearchOutlined,
  DownloadOutlined,
  TeamOutlined,
  FallOutlined,
  RiseOutlined,
  LineChartOutlined,
  EditOutlined,
  StopOutlined,
} from '@ant-design/icons';
import api from '../services/api';
import { formatVnDate, formatVnTime } from '../utils/vnDateTime';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

type VoucherStatus = 'active' | 'expired' | 'scheduled' | 'inactive';

interface VoucherDetailData {
  id: number;
  code: string;
  name?: string;
  restaurant_id: number | null;
  restaurant_name?: string;
  discount_type: 'percentage' | 'fixed_amount';
  discount_value: number;
  minimum_order_amount?: number | null;
  max_discount_amount?: number | null;
  valid_from: string;
  valid_until: string;
  is_active?: boolean;
  total_used?: number;
  total_discount_amount?: number | null;
}

interface UsageRow {
  id?: number;
  order_id: number;
  order_number?: string;
  customer_name?: string;
  customer_email?: string;
  merchant_name?: string;
  used_at: string;
  order_value: number;
  discount_amount: number;
}

interface UsageResponse {
  success?: boolean;
  data: UsageRow[];
  meta?: { total: number; page: number; limit: number };
}

function getVoucherStatus(
  validFrom: string,
  validUntil: string,
  isActive?: boolean
): VoucherStatus {
  if (isActive === false) return 'inactive';
  const now = Date.now();
  const start = new Date(validFrom).getTime();
  const end = new Date(validUntil).getTime();
  if (isNaN(start) || isNaN(end)) return 'expired';
  if (now < start) return 'scheduled';
  if (now > end) return 'expired';
  return 'active';
}

function formatDateRange(start: string, end: string): string {
  const d1 = formatVnDate(start, 'MMM DD, YYYY');
  const d2 = formatVnDate(end, 'MMM DD, YYYY');
  if (d1 === '—' || d2 === '—') return '–';
  return `${d1} - ${d2}`;
}

function discountBadgeText(
  discountType: string,
  discountValue: number
): string {
  const val =
    typeof discountValue === 'number' && !Number.isNaN(discountValue)
      ? discountValue
      : 0;
  if (discountType === 'percentage') return `${val}% OFF`;
  if (discountType === 'fixed_amount')
    return `${currencyFormatter.format(val)} Flat`;
  return `${val}% OFF`;
}

const AVATAR_COLORS = ['#e5e7eb', '#fce7f3', '#dbeafe', '#fed7aa', '#ccfbf1'];

function getInitials(name: string): string {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2)
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export default function VoucherDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [voucher, setVoucher] = useState<VoucherDetailData | null>(null);
  const [usageList, setUsageList] = useState<UsageRow[]>([]);
  const [usageTotal, setUsageTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [timeFilter, setTimeFilter] = useState<string>('all');
  const [merchantFilter, setMerchantFilter] = useState<string>('all');
  const [usagePage, setUsagePage] = useState(1);
  const [usageLimit] = useState(5);
  const [inactiveModalOpen, setInactiveModalOpen] = useState(false);
  const [inactivating, setInactivating] = useState(false);

  const loadVoucher = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.get<{ success?: boolean; data: VoucherDetailData }>(
        `/discount-codes/${String(id)}`
      );
      const data = res.data?.data ?? res.data;
      if (data) {
        setVoucher(data);
      } else {
        setError('Voucher not found');
      }
    } catch (err: unknown) {
      const ax = err as {
        response?: { status?: number; data?: { message?: string } };
      };
      if (ax.response?.status === 404) setError('Voucher not found');
      else setError(ax.response?.data?.message || 'Failed to load voucher');
      setVoucher(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadUsage = useCallback(async () => {
    if (!id) return;
    try {
      setUsageLoading(true);
      setUsageError(null);
      const params: Record<string, string | number> = {
        page: usagePage,
        limit: usageLimit,
      };
      if (searchText) params.search = searchText;
      const res = await api.get<UsageResponse>(`/discount-codes/${id}/usage`, {
        params,
      });
      const data = Array.isArray(res.data?.data)
        ? res.data.data
        : (res.data as unknown as UsageRow[]);
      const meta = (res.data as UsageResponse)?.meta;
      setUsageList(Array.isArray(data) ? data : []);
      setUsageTotal(
        typeof meta?.total === 'number'
          ? meta.total
          : Array.isArray(data)
          ? data.length
          : 0
      );
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      setUsageList([]);
      setUsageTotal(0);
      setUsageError(
        ax.response?.data?.message || 'Không tải được lịch sử sử dụng voucher'
      );
    } finally {
      setUsageLoading(false);
    }
  }, [id, usagePage, usageLimit, searchText]);

  useEffect(() => {
    const stateVoucher = (
      location.state as { voucher?: VoucherDetailData } | null
    )?.voucher;
    if (stateVoucher && String(stateVoucher.id) === String(id)) {
      setVoucher(stateVoucher);
      setLoading(false);
      setError(null);
    } else {
      loadVoucher();
    }
  }, [id, loadVoucher, location.state]);

  useEffect(() => {
    if (voucher || !loading) loadUsage();
  }, [voucher, loading, loadUsage]);

  const status = voucher
    ? getVoucherStatus(
        voucher.valid_from,
        voucher.valid_until,
        voucher.is_active
      )
    : 'expired';
  const statusLabel =
    status === 'active'
      ? 'Active'
      : status === 'scheduled'
      ? 'Scheduled'
      : status === 'inactive'
      ? 'Inactive'
      : 'Expired';
  const statusClass =
    status === 'active'
      ? 'voucher-detail-status-active'
      : status === 'scheduled'
      ? 'voucher-detail-status-scheduled'
      : status === 'inactive'
      ? 'voucher-detail-status-inactive'
      : 'voucher-detail-status-expired';

  const canDeactivate = status === 'active' || status === 'scheduled';
  const inactiveTooltip = !canDeactivate
    ? 'Voucher đã inactive hoặc expired'
    : undefined;

  const handleDeactivate = async () => {
    if (!id) return;
    try {
      setInactivating(true);
      await api.put(`/discount-codes/${id}`, { isActive: false });
      message.success('Voucher đã được deactivate');
      setInactiveModalOpen(false);
      await loadVoucher();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      message.error(
        ax.response?.data?.message || 'Failed to deactivate voucher'
      );
    } finally {
      setInactivating(false);
    }
  };

  const totalRedemptions =
    typeof voucher?.total_used === 'number' && !Number.isNaN(voucher.total_used)
      ? voucher.total_used
      : usageTotal;
  const discountGivenFromVoucher =
    voucher?.total_discount_amount != null
      ? Number(voucher.total_discount_amount)
      : NaN;
  const discountGiven = !Number.isNaN(discountGivenFromVoucher)
    ? discountGivenFromVoucher
    : usageList.reduce((sum, r) => sum + (Number(r.discount_amount) || 0), 0);
  const salesGenerated = usageList.reduce(
    (sum, r) => sum + (Number(r.order_value) || 0),
    0
  );
  const avgOrderValue =
    usageList.length > 0 ? salesGenerated / usageList.length : 0;

  const start = (usagePage - 1) * usageLimit + 1;
  const end = Math.min(usagePage * usageLimit, usageTotal);

  if (loading && !voucher) {
    return (
      <div className="voucher-detail-wrap">
        <div className="voucher-detail-breadcrumb">
          <Link to="/">Dashboard</Link>
          <span className="voucher-detail-breadcrumb-sep">&gt;</span>
          <Link to="/vouchers">Vouchers</Link>
          <span className="voucher-detail-breadcrumb-sep">&gt;</span>
          <span>Usage History</span>
        </div>
        <div className="voucher-detail-spin">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (error && !voucher) {
    return (
      <div className="voucher-detail-wrap">
        <div className="voucher-detail-breadcrumb">
          <Link to="/">Dashboard</Link>
          <span className="voucher-detail-breadcrumb-sep">&gt;</span>
          <Link to="/vouchers">Vouchers</Link>
        </div>
        <Alert
          message={error}
          type="warning"
          showIcon
          action={
            <Button size="small" onClick={() => navigate('/vouchers')}>
              Back to list
            </Button>
          }
        />
      </div>
    );
  }

  if (!voucher) return null;

  const minSpend =
    voucher.minimum_order_amount != null
      ? Number(voucher.minimum_order_amount)
      : 0;
  const maxCap =
    voucher.max_discount_amount != null
      ? Number(voucher.max_discount_amount)
      : null;
  const merchantLabel =
    voucher.restaurant_id == null
      ? 'Platform Wide'
      : voucher.restaurant_name || `Merchant ${voucher.restaurant_id}`;

  return (
    <div className="voucher-detail-wrap">
      <h1 className="voucher-detail-page-title">Voucher Management</h1>
      <div className="voucher-detail-breadcrumb">
        <Link to="/">Dashboard</Link>
        <span className="voucher-detail-breadcrumb-sep">&gt;</span>
        <Link to="/vouchers">Vouchers</Link>
        <span className="voucher-detail-breadcrumb-sep">&gt;</span>
        <span className="voucher-detail-breadcrumb-current">Usage History</span>
      </div>

      <div className="voucher-detail-header">
        <div className="voucher-detail-header-top">
          <div className="voucher-detail-header-left">
            <span className="voucher-detail-code">{voucher.code || '–'}</span>
            <span className={`voucher-detail-status-badge ${statusClass}`}>
              {statusLabel}
            </span>
            <span className="voucher-detail-discount-badge">
              {discountBadgeText(voucher.discount_type, voucher.discount_value)}
            </span>
          </div>
          <div className="voucher-detail-header-actions">
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/vouchers/${id}/edit`)}
            >
              Edit
            </Button>
            <Tooltip title={inactiveTooltip}>
              <span>
                <Button
                  danger
                  type="default"
                  icon={<StopOutlined />}
                  disabled={!canDeactivate}
                  onClick={() => setInactiveModalOpen(true)}
                >
                  Inactive
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>
        <div className="voucher-detail-header-meta">
          <span className="voucher-detail-meta-item">
            <CalendarOutlined className="voucher-detail-meta-icon" />
            {formatDateRange(voucher.valid_from, voucher.valid_until)}
          </span>
          <span className="voucher-detail-meta-item">
            <DollarOutlined className="voucher-detail-meta-icon" />
            Min. Spend {currencyFormatter.format(minSpend)}
            {maxCap != null && !Number.isNaN(maxCap)
              ? ` • Max Cap ${currencyFormatter.format(maxCap)}`
              : ''}
          </span>
          <span className="voucher-detail-meta-item">
            <ShopOutlined className="voucher-detail-meta-icon" />
            {merchantLabel}
          </span>
        </div>
      </div>

      <Modal
        title="Deactivate voucher?"
        open={inactiveModalOpen}
        onCancel={() => setInactiveModalOpen(false)}
        onOk={handleDeactivate}
        okText="Deactivate"
        okButtonProps={{ danger: true, loading: inactivating }}
        cancelButtonProps={{ disabled: inactivating }}
      >
        <p>
          Voucher sẽ không thể sử dụng cho đơn hàng mới. Hành động này có thể
          không hoàn tác.
        </p>
      </Modal>

      <div className="voucher-detail-stats">
        <Card className="voucher-detail-stat-card">
          <div className="voucher-detail-stat-inner">
            <div className="voucher-detail-stat-text">
              <div className="voucher-detail-stat-title">Total Redemptions</div>
              <div className="voucher-detail-stat-value">
                {totalRedemptions.toLocaleString()}
              </div>
            </div>
            <div className="voucher-detail-stat-icon voucher-detail-stat-icon-blue">
              <TeamOutlined />
            </div>
          </div>
        </Card>
        <Card className="voucher-detail-stat-card">
          <div className="voucher-detail-stat-inner">
            <div className="voucher-detail-stat-text">
              <div className="voucher-detail-stat-title">Discount Given</div>
              <div className="voucher-detail-stat-value">
                {currencyFormatter.format(discountGiven)}
              </div>
            </div>
            <div className="voucher-detail-stat-icon voucher-detail-stat-icon-purple">
              <FallOutlined />
            </div>
          </div>
        </Card>
        <Card className="voucher-detail-stat-card">
          <div className="voucher-detail-stat-inner">
            <div className="voucher-detail-stat-text">
              <div className="voucher-detail-stat-title">Sales Generated</div>
              <div className="voucher-detail-stat-value">
                {currencyFormatter.format(salesGenerated)}
              </div>
            </div>
            <div className="voucher-detail-stat-icon voucher-detail-stat-icon-green">
              <RiseOutlined />
            </div>
          </div>
        </Card>
        <Card className="voucher-detail-stat-card">
          <div className="voucher-detail-stat-inner">
            <div className="voucher-detail-stat-text">
              <div className="voucher-detail-stat-title">Avg. Order Value</div>
              <div className="voucher-detail-stat-value">
                {currencyFormatter.format(avgOrderValue)}
              </div>
            </div>
            <div className="voucher-detail-stat-icon voucher-detail-stat-icon-yellow">
              <LineChartOutlined />
            </div>
          </div>
        </Card>
      </div>

      <div className="voucher-detail-filter-bar">
        <Input
          placeholder="Search Order ID or Customer..."
          prefix={
            <SearchOutlined className="voucher-detail-filter-search-icon" />
          }
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          onPressEnter={() => loadUsage()}
          allowClear
          className="voucher-detail-filter-search"
        />
        <div className="voucher-detail-filter-right">
          <Select
            value={timeFilter}
            onChange={setTimeFilter}
            options={[{ value: 'all', label: 'Time' }]}
            className="voucher-detail-filter-select"
          />
          <Select
            value={merchantFilter}
            onChange={setMerchantFilter}
            options={[{ value: 'all', label: 'All Merchants' }]}
            className="voucher-detail-filter-select"
          />
          <Button
            icon={<DownloadOutlined />}
            className="voucher-detail-export-btn"
          >
            Export Log
          </Button>
        </div>
      </div>

      <Card className="voucher-detail-table-card">
        {usageError ? (
          <Alert
            type="warning"
            showIcon
            message={usageError}
            style={{ marginBottom: 16 }}
          />
        ) : null}
        <Table<UsageRow>
          loading={usageLoading}
          dataSource={usageList}
          rowKey={r => `${r.order_id}-${r.used_at}`}
          pagination={false}
          size="middle"
          columns={[
            {
              title: 'ORDER ID',
              dataIndex: 'order_id',
              key: 'order_id',
              render: (_: unknown, r: UsageRow) => (
                <Link
                  to={`/orders/${r.order_id}`}
                  className="voucher-detail-order-link"
                >
                  {r.order_number ? `#${r.order_number}` : '–'}
                </Link>
              ),
            },
            {
              title: 'CUSTOMER NAME',
              key: 'customer',
              render: (_: unknown, r: UsageRow) => {
                const name = r.customer_name || '–';
                const email = r.customer_email || '';
                const initials = getInitials(name);
                const colorIndex =
                  Math.abs(
                    (name + r.order_id)
                      .split('')
                      .reduce((a, c) => a + c.charCodeAt(0), 0)
                  ) % AVATAR_COLORS.length;
                return (
                  <div className="voucher-detail-customer-cell">
                    <Avatar
                      size={36}
                      className="voucher-detail-customer-avatar"
                      style={{
                        backgroundColor: AVATAR_COLORS[colorIndex],
                        color: '#374151',
                      }}
                    >
                      {initials}
                    </Avatar>
                    <div className="voucher-detail-customer-info">
                      <span className="voucher-detail-customer-name">
                        {name}
                      </span>
                      {email && (
                        <span className="voucher-detail-customer-email">
                          {email}
                        </span>
                      )}
                    </div>
                  </div>
                );
              },
            },
            {
              title: 'MERCHANT NAME',
              dataIndex: 'merchant_name',
              key: 'merchant_name',
              render: (val: string) => (
                <span className="voucher-detail-merchant-cell">
                  <ShopOutlined className="voucher-detail-merchant-icon" />
                  {val || '–'}
                </span>
              ),
            },
            {
              title: 'USAGE DATE/TIME',
              dataIndex: 'used_at',
              key: 'used_at',
              render: (val: string) => {
                if (!val) return '–';
                const datePart = formatVnDate(val, 'MMM DD, YYYY');
                const timePart = formatVnTime(val, 'hh:mm A');
                if (datePart === '—') return '–';
                return (
                  <span className="voucher-detail-datetime-cell">
                    <span>{datePart}</span>
                    <span className="voucher-detail-datetime-time">
                      {timePart}
                    </span>
                  </span>
                );
              },
            },
            {
              title: 'ORDER VALUE',
              dataIndex: 'order_value',
              key: 'order_value',
              align: 'right',
              render: (val: number) => (
                <span className="voucher-detail-order-value">
                  {typeof val === 'number' && !Number.isNaN(val)
                    ? currencyFormatter.format(val)
                    : '–'}
                </span>
              ),
            },
            {
              title: 'DISCOUNT',
              dataIndex: 'discount_amount',
              key: 'discount_amount',
              align: 'right',
              render: (val: number) => {
                const n =
                  typeof val === 'number' && !Number.isNaN(val) ? val : 0;
                return (
                  <span className="voucher-detail-discount-cell">
                    -{currencyFormatter.format(n)}
                  </span>
                );
              },
            },
          ]}
        />
        <div className="voucher-detail-pagination">
          <span className="voucher-detail-pagination-info">
            Showing <strong>{usageTotal === 0 ? 0 : start}</strong> to{' '}
            <strong>{end}</strong> of <strong>{usageTotal}</strong> results
          </span>
          <div className="voucher-detail-pagination-btns">
            <Button
              disabled={usagePage <= 1}
              onClick={() => setUsagePage(p => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              disabled={usagePage * usageLimit >= usageTotal}
              onClick={() => setUsagePage(p => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
